import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Download } from "lucide-react";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCycleWiseRun } from "../hooks";
interface BillCycleRunsTableProps {
  billCycleId: string;
}

export function BillCycleRunsTable() {
  const params = useParams();
  const billCycleId = params.id;
  const action = params.action;
  const [searchTerm, setSearchTerm] = useState("");
  const [timePeriod, setTimePeriod] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { data: runList } = useCycleWiseRun(
    billCycleId,
    currentPage,
    searchTerm.length > 2 ? searchTerm : undefined
    // timePeriod
  );

  const currency = useCurrency();

  const navigate = useNavigate();

  const handleViewReport = (run: any) => {
    navigate(`/bx/bill-setup/billcycle/summary/${run.id}`);
  };

  const handleSearch = (searchValue: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: "startDate",
      header: "Run Date",
      sortable: true,
      render: (value: string) => (
        <span className="text-sm font-medium">{value}</span>
      ),
    },
    {
      key: "lastModifiedUserRemoteName",
      header: "Initiated By",
      sortable: true,
    },
    {
      key: "daysBetweenRuns",
      header: "Days Between Runs",
      render: (value: string) => (
        <span className="text-sm text-gray-600">{value ?? "N/A"} days</span>
      ),
    },
    {
      key: "consumerCount",
      header: "Consumers",
    },
    {
      key: "actualVsEstimated",
      header: "Actual vs Estimate",
      render: (value) => (
        <span className="text-sm">
          <span className="font-medium">{value?.actualPercentage} %</span>
          <span className="text-gray-500 ml-1">Actual</span>
        </span>
      ),
    },
    {
      key: "billedAmount",
      header: "Billed Amount",
      sortable: true,
      render: (value: string) => (
        <div>
          {currency} {value}
        </div>
      ),
    },
  ];

  const tableActions = [
    {
      label: "View Report",
      variant: "outline" as const,
      onClick: (row: any, index: number) => {
        handleViewReport(row);
      },
    },
  ];

  // Bulk actions for selected rows
  const bulkActions = [
    {
      label: "Export Selected",
      variant: "outline" as const,
      icon: <Download size={16} />,
      onClick: (selectedData: any[], selectedRowIds: string[]) => {
        console.log(
          "Bulk export:",
          selectedData.map((r) => r.id)
        );
        setSelectedRows([]);
      },
    },
  ];

  // Time period filter options
  const timePeriodOptions = [
    { value: "all", label: "All Time" },
    { value: "this_month", label: "This Month" },
    { value: "last_month", label: "Last Month" },
    { value: "last_3_months", label: "Last 3 Months" },
    { value: "last_6_months", label: "Last 6 Months" },
  ];

  // Custom filters component
  const CustomFilters = () => (
    <div className="flex gap-2">
      <Select
        value={timePeriod}
        onValueChange={(value) => {
          setTimePeriod(value);
          setCurrentPage(1); // Reset to first page when filtering
        }}
      >
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Time Period" />
        </SelectTrigger>
        <SelectContent>
          {timePeriodOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  const isSelectable = params.action == "edit";

  return (
    <div className="">
      <GenericTable
        data={runList?.result ?? []}
        columns={columns}
        rowKey="id"
        actions={tableActions}
        actionsType="buttons"
        emptyMessage="No run history available for this bill cycle"
        selectable={isSelectable}
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        bulkActions={bulkActions}
        filters={<CustomFilters />}
        onSearch={handleSearch}
        searchPlaceholder="Search by initiator..."
        pagination={{
          currentPage,
          totalPages: Math.ceil(runList?.count / PAGE_SIZE),
          totalItems: runList?.count,
          itemsPerPage: PAGE_SIZE,
          startItem: (currentPage - 1) * PAGE_SIZE + 1,
          endItem: Math.min(currentPage * PAGE_SIZE, runList?.count),
        }}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
